import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEmployeeLoginData } from '~/data/employee';
import { Roles } from '~/data/user';

import { putEmpRoles } from './data';

export type IFormInput = {
  fUserRoles?: string[];
};

export type EmpRolesType = {
  EMPLOYEE?: boolean;
  DEPUTY_MANAGER?: boolean;
  IT_ADMIN?: boolean;
  ALT_MANAGER?: boolean;
  MANAGER?: boolean;
  HR_DEV?: boolean;
  HR_STAFF?: boolean;
  HR_ADMIN?: boolean;
  HR_ADMIN_LIMITED?: boolean;
  HR_SALARY?: boolean;
  RECRUITER?: boolean;
};

export type useEmployeeDataOutput = {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  empRoles: EmpRolesType;
};

export const useChangeRole = (props: {
  onSubmitSuccess?: () => void;
}): useEmployeeDataOutput => {
  const { onSubmitSuccess } = props;
  const { t } = useTranslation();
  const {
    employeeAdminLoginData: employeeLoginData,
    loginDataLoading: isLoading,
    loginDataFailedLoading: failedToLoad,
  } = useEmployeeLoginData();
  const checkIfHasRole = useCallback(
    (role: string) => employeeLoginData.fLoginRoles.includes(role),
    [employeeLoginData.fLoginRoles],
  );

  const [empRoles, setEmpRoles] = useState<EmpRolesType>({});
  const [defaultRoles, setDefaultRoles] = useState<EmpRolesType>({});

  useEffect(() => {
    if (
      !isLoading &&
      !failedToLoad &&
      Object.keys(employeeLoginData).length > 0
    ) {
      const roles = {
        EMPLOYEE: checkIfHasRole(Roles.EMPLOYEE),
        DEPUTY_MANAGER: checkIfHasRole(Roles.DEPUTY_MANAGER),
        IT_ADMIN: checkIfHasRole(Roles.IT_ADMIN),
        ALT_MANAGER: checkIfHasRole(Roles.ALT_MANAGER),
        MANAGER: checkIfHasRole(Roles.MANAGER),
        HR_DEV: checkIfHasRole(Roles.HR_DEV),
        HR_STAFF: checkIfHasRole(Roles.HR_STAFF),
        HR_ADMIN: checkIfHasRole(Roles.HR_ADMIN),
        HR_SALARY: checkIfHasRole(Roles.HR_SALARY),
        RECRUITER: checkIfHasRole(Roles.RECRUITER),
      };
      setEmpRoles(roles);
      setDefaultRoles(roles);
    }
  }, [checkIfHasRole, employeeLoginData, failedToLoad, isLoading]);

  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const isChecked = e.target.checked;
    if (isChecked) {
      switch (val) {
        case Roles.EMPLOYEE:
          setEmpRoles({
            EMPLOYEE: true,
            DEPUTY_MANAGER: false,
            IT_ADMIN: false,
            ALT_MANAGER: false,
            MANAGER: false,
            HR_DEV: false,
            HR_STAFF: false,
            HR_ADMIN: false,
            HR_ADMIN_LIMITED: false,
            HR_SALARY: false,
            RECRUITER: false,
          });
          break;
        case Roles.IT_ADMIN:
          setEmpRoles({
            EMPLOYEE: false,
            MANAGER: empRoles.MANAGER,
            DEPUTY_MANAGER: false,
            IT_ADMIN: true,
            ALT_MANAGER: false,
            HR_DEV: false,
            HR_STAFF: false,
            HR_ADMIN: false,
            HR_ADMIN_LIMITED: false,
            HR_SALARY: false,
            RECRUITER: false,
          });
          break;
        case Roles.DEPUTY_MANAGER:
          setEmpRoles({
            DEPUTY_MANAGER: true,
            IT_ADMIN: false,
            HR_DEV: false,
            HR_STAFF: false,
            HR_ADMIN: false,
            HR_ADMIN_LIMITED: false,
            HR_SALARY: false,
            RECRUITER: false,
            EMPLOYEE: false,
            MANAGER: empRoles.MANAGER,
            ALT_MANAGER: empRoles.ALT_MANAGER,
          });
          break;
        case Roles.ALT_MANAGER:
          setEmpRoles({
            ALT_MANAGER: true,
            IT_ADMIN: false,
            HR_DEV: false,
            HR_SALARY: false,
            RECRUITER: false,
            EMPLOYEE: false,
            MANAGER: empRoles.MANAGER,
            DEPUTY_MANAGER: empRoles.DEPUTY_MANAGER,
            HR_STAFF: empRoles.HR_STAFF,
            HR_ADMIN: empRoles.HR_ADMIN,
            HR_ADMIN_LIMITED: empRoles.HR_ADMIN_LIMITED,
          });
          break;
        case Roles.MANAGER:
          setEmpRoles({
            MANAGER: true,
            HR_DEV: false,
            HR_SALARY: false,
            RECRUITER: false,
            EMPLOYEE: false,
            DEPUTY_MANAGER: empRoles.DEPUTY_MANAGER,
            IT_ADMIN: empRoles.IT_ADMIN,
            ALT_MANAGER: empRoles.ALT_MANAGER,
            HR_STAFF: empRoles.HR_STAFF,
            HR_ADMIN: empRoles.HR_ADMIN,
            HR_ADMIN_LIMITED: empRoles.HR_ADMIN_LIMITED,
          });
          break;
        case Roles.RECRUITER:
          setEmpRoles({
            RECRUITER: true,
            IT_ADMIN: false,
            DEPUTY_MANAGER: false,
            ALT_MANAGER: false,
            MANAGER: false,
            HR_DEV: false,
            HR_STAFF: false,
            HR_ADMIN: false,
            HR_ADMIN_LIMITED: false,
            HR_SALARY: false,
            EMPLOYEE: false,
          });
          break;
        case Roles.HR_DEV:
          setEmpRoles({
            HR_DEV: true,
            ALT_MANAGER: false,
            MANAGER: false,
            HR_STAFF: false,
            HR_ADMIN: false,
            HR_ADMIN_LIMITED: false,
            HR_SALARY: false,
            RECRUITER: false,
            EMPLOYEE: false,
            DEPUTY_MANAGER: false,
            IT_ADMIN: false,
          });
          break;
        case Roles.HR_STAFF:
          setEmpRoles({
            HR_STAFF: true,
            HR_DEV: false,
            HR_ADMIN: false,
            HR_ADMIN_LIMITED: false,
            HR_SALARY: false,
            RECRUITER: false,
            EMPLOYEE: false,
            MANAGER: empRoles.MANAGER,
            DEPUTY_MANAGER: false,
            IT_ADMIN: false,
            ALT_MANAGER: empRoles.ALT_MANAGER,
          });
          break;
        case Roles.HR_ADMIN:
          setEmpRoles({
            HR_ADMIN: true,
            HR_ADMIN_LIMITED: false,
            HR_DEV: false,
            HR_STAFF: false,
            HR_SALARY: false,
            RECRUITER: false,
            EMPLOYEE: false,
            MANAGER: empRoles.MANAGER,
            DEPUTY_MANAGER: false,
            IT_ADMIN: false,
            ALT_MANAGER: empRoles.ALT_MANAGER,
          });
          break;
        case Roles.HR_ADMIN_LIMITED:
          setEmpRoles({
            HR_ADMIN: false,
            HR_ADMIN_LIMITED: true,
            HR_DEV: false,
            HR_STAFF: false,
            HR_SALARY: false,
            RECRUITER: false,
            EMPLOYEE: false,
            MANAGER: empRoles.MANAGER,
            DEPUTY_MANAGER: false,
            IT_ADMIN: false,
            ALT_MANAGER: empRoles.ALT_MANAGER,
          });
          break;
        case Roles.HR_SALARY:
          setEmpRoles({
            HR_SALARY: true,
            ALT_MANAGER: false,
            MANAGER: false,
            HR_DEV: false,
            HR_STAFF: false,
            HR_ADMIN: false,
            HR_ADMIN_LIMITED: false,
            RECRUITER: false,
            EMPLOYEE: false,
            DEPUTY_MANAGER: false,
            IT_ADMIN: false,
          });
          break;
      }
    } else {
      setEmpRoles({ ...empRoles, [val]: false });
    }
  };

  const handleFormSubmit = (postData: string[]) => {
    (async () => {
      try {
        const response = await putEmpRoles(
          employeeLoginData.fLoginUsername,
          postData,
        );
        if (response) {
          if (!!onSubmitSuccess) {
            onSubmitSuccess();
          }
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
        }
      } catch (error) {
        enqueueSnackbar(t('laErrorRemoveRoleResponsibilities'), {
          variant: 'error',
        });
        setEmpRoles(defaultRoles);
      }
    })();
  };

  const onSubmit = () => {
    const array = Object.keys(empRoles).filter(
      (key) => empRoles[key as keyof EmpRolesType] === true,
    );
    handleFormSubmit(array);
  };

  return {
    handleChange,
    onSubmit,
    empRoles,
  };
};
